import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React from 'react';

import { UpdateLoader } from '~src/commons/components';

import { StateResponse } from '../../api/api';
import { GeneralError } from '../commons/components/general-error';
import {
  apiInstance,
  useGlobalState,
  useGlobalStateUpdate,
  useIsHardMutating,
} from '../global-state';
import { useIpc } from '../utils/ipc-utils';
import { LocationLostModal, SelectedChoiceLostModal } from './category-lost-modal';
import {
  checkCategoryLost,
  checkChoiceLost,
  compareNewAndOldSessionsById,
} from './check-category-lost';

const LegacyWidgetLoader: React.FC = () => {
  const updateGlobalState = useGlobalStateUpdate();
  const ipc = useIpc();
  const isMutating = useIsHardMutating();
  const oldState = useGlobalState();

  const [isSuspended, setIsSuspended] = React.useState(false);
  const [categoryLost, setCategoryLost] = React.useState(false);
  const [locationLost, setLocationLost] = React.useState<boolean>(false);

  const mutationFn = async () => {
    const { data } = await apiInstance.v4.stateGet(oldState.checkout_session_id);
    return data;
  };

  /**
   * We query the state as a fake mutation because this usecase does not really
   * fit the 'useQuery' hook. We cannot use 'globalStateQuery.refetch' because
   * we need a separate query key to trigger 'onSuccess' only in this scenario.
   */
  const reloadMutation = useMutation<StateResponse, AxiosError>({
    mutationFn,
    mutationKey: ['user-controlled-state-reload'],
    onSuccess: (newState: StateResponse) => {
      if (newState.sessions[0].flags.disable_choice_lost_modal_if_similar_selected) {
        if (compareNewAndOldSessionsById(newState, oldState)) {
          setCategoryLost(true);
        }
      } else {
        if (checkCategoryLost(newState, oldState)) {
          setCategoryLost(true);
        } else if (checkChoiceLost(newState, oldState)) {
          setLocationLost(true);
        }
      }

      updateGlobalState(newState);
    },
  });

  React.useEffect(() => {
    ipc.listen(payload => {
      if (payload.name === 'suspend') {
        setIsSuspended(true);
      }
      if (payload.name === 'resume') {
        reloadMutation.mutate();
        setIsSuspended(false);
      }
    });
  }, []);

  return (
    <>
      {(isMutating || isSuspended) && <UpdateLoader />}
      <GeneralError error={reloadMutation.error} onClose={reloadMutation.reset} />
      {categoryLost && <SelectedChoiceLostModal onClose={() => setCategoryLost(false)} />}
      {oldState.sessions[0].flags.show_location_unavailable_modal_message && locationLost && (
        <LocationLostModal
          onClose={() => {
            setLocationLost(false);
          }}
        />
      )}
    </>
  );
};

const GlobalWidgetLoader: React.FC = () => {
  const isMutating = useIsHardMutating();

  if (isMutating) {
    return <UpdateLoader />;
  }

  return null;
};

export const WidgetLoader: React.FC = () => {
  const globalState = useGlobalState();
  // TODO: move the config to global state.
  const session = globalState.sessions[0];

  if (session.flags.send_state_events) {
    return <GlobalWidgetLoader />;
  }

  return <LegacyWidgetLoader />;
};
